import { ISkillLayout } from './skillGraphLayout';
import { ISkillGraphIndex } from './skillGraphModel';

/**
 * Перемещение фокуса стрелками по карте (ТЗ 0070, доступность).
 *
 * Чистая функция, потому что «стрелка вправо ведёт не туда» — дефект, который
 * в браузере ловится случайно, а в тесте формулируется одной строкой.
 *
 * Правило простое и объяснимое вслух: вправо — вглубь (колонка → её первый
 * навык, навык → первая зависимость, если связи включены), влево — к родителю,
 * вверх и вниз — по соседям в той же колонке.
 *
 * 🔴 На краях НЕ заворачиваем. Прыжок с последнего элемента колонки на первый
 * читается как сбой: человек, который держит стрелку нажатой, теряет место и
 * начинает искать, куда его унесло.
 */
export type ArrowKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight';

export const nextFocus = (
  index: ISkillGraphIndex,
  layout: ISkillLayout,
  current: string | null,
  key: ArrowKey,
  options: { deps: boolean } = { deps: false },
): string | null => {
  const { cards } = layout;
  if (!cards.length) return null;
  const from = current ? layout.byKey.get(current) : undefined;
  if (!from) {
    // Фокуса ещё нет — входим в карту с самой левой карточки.
    const sorted = [...cards].sort((a, b) => a.level - b.level || a.y - b.y);
    return sorted[0]?.key ?? null;
  }

  if (key === 'ArrowRight') {
    const child = cards.filter((row) => row.parentKey === from.key).sort((a, b) => a.y - b.y)[0];
    if (child) return child.key;
    if (options.deps && from.kind === 'skill') {
      const edge = (index.opensOut.get(from.key) || []).find((row) => layout.byKey.has(row.to));
      if (edge) return edge.to;
    }
    return null;
  }

  if (key === 'ArrowLeft') {
    if (from.parentKey && layout.byKey.has(from.parentKey)) return from.parentKey;
    if (options.deps && from.kind === 'skill') {
      const edge = (index.needsIn.get(from.key) || []).find((row) => layout.byKey.has(row.from));
      if (edge) return edge.from;
    }
    return null;
  }

  const column = cards
    .filter((row) => row.level === from.level)
    .sort((a, b) => a.y - b.y || (a.key < b.key ? -1 : 1));
  const at = column.findIndex((row) => row.key === from.key);
  if (at < 0) return null;
  const to = key === 'ArrowUp' ? at - 1 : at + 1;
  if (to < 0 || to >= column.length) return null;
  return column[to].key;
};

/**
 * Хлебные крошки: профессия → группа → навык.
 *
 * Предков на холсте нет намеренно (см. `skillGraphLayout`), и это ровно то
 * место, где они живут вместо канваса.
 *
 * 🔴 Тропа строится и от ФОКУСА, а не только от выбранного навыка. Раньше
 * клик по карточке группы (или по заглушке «ещё N») сужал карту до одной
 * ветки, а крошки этого не замечали: единственная ступень оставалась
 * некликабельной, и вернуться ко всей карте из интерфейса было НЕЧЕМ —
 * помогала только кнопка «назад» в браузере.
 */
export const breadcrumbsFor = (
  index: ISkillGraphIndex,
  position: { focus: string | null; selected: string | null },
): Array<{ key: string; label: string }> => {
  const trail = [{ key: 'track', label: index.trackTitle }];

  const node = position.selected ? index.byId.get(position.selected) : null;
  const code = node ? node.clusterCode : position.focus;
  if (!code) return trail;

  const cluster = index.clusters.find((row) => row.code === code);
  if (cluster && cluster.title) {
    trail.push({ key: `cluster:${cluster.code}`, label: cluster.title });
  }
  if (node) trail.push({ key: node.id, label: node.name });
  return trail;
};
